import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { TabComponent } from '../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../components/tab-layout/tab-layout.component';
import { APITableComponent } from '../../../components/table/api-table/api-table.component';
import { ButtonDirective } from '../../../components/button/button.directive';
import { NgIcon } from '@ng-icons/core';
import { TableRow } from '../../../components/table/table.interface';
import { ModalComponent } from '../../../components/modal/modal.component';
import { CustomerService } from '../../../services/customer.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { CustomerDropdownCacheService } from '../../../services/customer-dropdown-cache.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { canManageCustomerDrafts } from '../../../utils/customer-draft-roles';
import {
  CustomerEditModalComponent,
  CustomerData,
} from './customer-edit-modal/customer-edit-modal.component';

@Component({
  selector: 'app-customers-tab',
  imports: [
    TabLayoutComponent,
    APITableComponent,
    ButtonDirective,
    NgIcon,
    ModalComponent,
    CustomerEditModalComponent,
  ],
  providers: [{ provide: TabComponent, useExisting: CustomersTabComponent }],
  templateUrl: './customers-tab.component.html',
  styleUrl: './customers-tab.component.scss',
})
export class CustomersTabComponent extends TabComponent implements OnInit {
  override name = this.dictionaryService.get('Customers');

  customerService = inject(CustomerService);
  snackbar = inject(SnackbarService);
  dropdownCache = inject(CustomerDropdownCacheService);
  authService = inject(AuthenticationService);
  deleteConfirmationModal = signal<boolean>(false);
  customersToDelete: TableRow[] = [];

  /** Whether the current role can create, edit, and delete customer drafts. */
  canManageDrafts = false;

  // Table reference for refresh
  @ViewChild('customersTable') customersTable?: APITableComponent;

  // Edit modal state
  editModalOpened = signal<boolean>(false);
  selectedCustomer = signal<CustomerData | null>(null);

  async ngOnInit(): Promise<void> {
    const userData = await this.authService.getUserData();
    this.canManageDrafts = canManageCustomerDrafts(userData?.role);
  }

  public async main(): Promise<void> {
    // Dropdown data will be loaded lazily when modals are first opened
  }

  handleRowClick = (row: TableRow) => {
    const rowValue = row.value;
    const isDeleted = Boolean(rowValue['isDeleted']);

    if (!this.canManageDrafts) {
      this.snackbar.showWarning(
        this.dictionaryService.get('SupervisorsCannotEdit') || 'Supervisors cannot edit customers'
      );
      return;
    }

    // Restriction 2: Deleted rows can't be edited
    if (isDeleted) {
      this.snackbar.showWarning(
        this.dictionaryService.get('DeletedCustomersCannotBeEdited') || 'Deleted customers cannot be edited'
      );
      return;
    }

    const customerId = String(rowValue['customerId'] ?? '');
    const name = String(rowValue['Name'] ?? '');
    const email = rowValue['Email'];
    const phone = rowValue['Phone'];
    const classType = String(rowValue['Company Class'] ?? '');
    const mktClass = String(rowValue['MKT Class'] ?? '');
    const specialClass = String(rowValue['Special Class'] ?? '');
    const speciality = rowValue['Speciality'];
    const type = String(rowValue['Type'] ?? '');
    const center = rowValue['Center'];
    const country = String(rowValue['Country'] ?? '');
    const province = String(rowValue['Province'] ?? '');
    const city = String(rowValue['City'] ?? '');
    const targetedFrequency = Number(rowValue['Targeted Frequency']) || 0;

    const data: CustomerData = {
      customerId,
      name,
      email: email ? String(email) : undefined,
      phone: phone ? String(phone) : undefined,
      class: classType,
      mktClass,
      specialClass,
      speciality: speciality ? String(speciality) : undefined,
      type,
      center: center ? String(center) : undefined,
      country,
      province,
      city,
      targetedFrequency,
      isDeleted,
    };
    this.selectedCustomer.set(data);
    this.editModalOpened.set(true);
  };

  handleEditModalClose = () => {
    this.editModalOpened.set(false);
    this.selectedCustomer.set(null);
  };

  handleEditSave = () => {
    // Refresh customers table after successful edit
    this.customersTable?.refresh();
    this.editModalOpened.set(false);
    this.selectedCustomer.set(null);
  };

  deleteRow = (row: TableRow) => {
    if (!this.canManageDrafts) {
      return;
    }

    this.deleteConfirmationModal.set(true);
    this.customersToDelete = [row];
  };

  deleteSelected = (rows: TableRow[]) => {
    if (!this.canManageDrafts) {
      return;
    }

    this.deleteConfirmationModal.set(true);
    this.customersToDelete = rows;
  };

  deleteConfirmed = () => {
    if (!this.canManageDrafts) {
      this.deleteConfirmationModal.set(false);
      this.customersToDelete = [];
      return;
    }

    this.customerService
      .addToDeleteDraft(
        this.customersToDelete.map(
          (customer) => customer.value['customerId'] as string,
        ),
      )
      .subscribe({
        next: (res: any) => {
          this.snackbar.showByCode(res?.messageCode ?? 'S0803001');
          // Trigger refresh of the Deletion draft tab
          this.customerService.triggerDraftTableRefresh('Deletion');
        },
        error: (err) => {
          if (err.error?.messageCode) {
            this.snackbar.showByCode(err.error.messageCode);
          } else {
            this.snackbar.showError(err.error?.message || 'Failed to add to delete draft');
          }
        },
      });
    this.deleteConfirmationModal.set(false);
    this.customersToDelete = [];
  };
}
